import { useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';
import { ConfirmModal, NotificationToast } from '../components/common';
import { TRANSLATIONS } from '../config/translations';

const db = firebase.firestore();
const functions = firebase.functions();

/**
 * Users view - manage user roles and permissions (admin only)
 */
export function UsersView({ language }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changingRole, setChangingRole] = useState(null);
  const [forcingRevert, setForcingRevert] = useState(null);
  const [roleChangeModal, setRoleChangeModal] = useState(null);
  const [notification, setNotification] = useState(null);
  const t = TRANSLATIONS[language] || TRANSLATIONS.de;

  useEffect(() => {
    const unsubscribe = db.collection('users')
      .limit(500)
      .onSnapshot((snapshot) => {
        const usersData = [];
        snapshot.forEach((doc) => {
          usersData.push({ id: doc.id, ...doc.data() });
        });
        setUsers(usersData);
        setLoading(false);
      }, () => setLoading(false));

    return () => unsubscribe();
  }, []);

  const handleChangeRole = (user, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    setRoleChangeModal({ user, newRole, currentRole });
  };

  const confirmRoleChange = async () => {
    const { user, newRole } = roleChangeModal;
    setRoleChangeModal(null);
    setChangingRole(user.id);
    
    try {
      const updateUserRole = functions.httpsCallable('setUserRole');
      await updateUserRole({ uid: user.id, role: newRole });
      setNotification({ message: 'Rolle erfolgreich geändert!', type: 'success' });
    } catch (error) {
      // console statement removed for production
      setNotification({ message: 'Fehler beim Ändern der Rolle: ' + error.message, type: 'error' });
    } finally {
      setChangingRole(null);
    }
  };

  const handleForceRevert = async (userId) => {
    setForcingRevert(userId);
    try {
      const forceRevert = functions.httpsCallable('forceRevertAbsence');
      const result = await forceRevert({ absentUserId: userId });
      setNotification({ message: result.data.message, type: 'success' });
    } catch (error) {
      // console statement removed for production
      setNotification({ message: 'Fehler beim Beenden der Abwesenheit: ' + error.message, type: 'error' });
    } finally {
      setForcingRevert(null);
    }
  };

  if (loading) {
    return <div className="loading">{t.loading}</div>;
  }

  return (
    <div className="users-view">
      <div className="view-header">
        <h2>{t.users}</h2>
        <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: 0 }}>
          Benutzer erstellen sich automatisch beim ersten Login mit Microsoft
        </p>
      </div>

      <div className="user-table-container">
        <table className="user-table">
          <thead>
            <tr>
              <th>{t.name}</th>
              <th>{t.email}</th>
              <th>{t.role}</th>
              <th>Anwesend</th>
              <th>{t.status}</th>
              <th>{t.actions}</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="user-name-cell">
                    <div className="user-avatar-small">
                      {user.displayName?.[0]?.toUpperCase() || '?'}
                    </div>
                    <strong>{user.displayName}</strong>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  <span className={`role-badge role-${user.role}`}>
                    {user.role === 'admin' ? 'Admin' : 'Benutzer'}
                  </span>
                </td>
                <td>
                  <span className={`status-badge status-${user.isAbsent ? 'inactive' : 'active'}`}>
                    {user.isAbsent ? 'Nein' : 'Ja'}
                  </span>
                </td>
                <td>
                  <span className={`status-badge status-${user.active ? 'active' : 'inactive'}`}>
                    {user.active ? t.active : t.inactive}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className={user.role === 'admin' ? 'btn-secondary' : 'btn-primary'}
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                      onClick={() => handleChangeRole(user, user.role)}
                      disabled={changingRole === user.id}
                    >
                      {changingRole === user.id ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Lädt...
                        </>
                      ) : user.role === 'admin' ? (
                        <>
                          <i className="fas fa-user"></i> Zu Benutzer
                        </>
                      ) : (
                        <>
                          <i className="fas fa-crown"></i> Zu Admin
                        </>
                      )}
                    </button>

                    {user.isAbsent && (
                      <button
                        className="btn-warning"
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                        onClick={() => handleForceRevert(user.id)}
                        disabled={forcingRevert === user.id}
                      >
                        {forcingRevert === user.id ? (
                          <><i className="fas fa-spinner fa-spin"></i> ...</>
                        ) : (
                          <><i className="fas fa-undo"></i> Wieder anwesend</>
                        )}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {roleChangeModal && (
        <ConfirmModal
          title="Rolle ändern"
          message={`Möchten Sie ${roleChangeModal.user.displayName} wirklich ${
            roleChangeModal.newRole === 'admin' 
              ? 'zum Admin machen? Dies gibt volle Berechtigungen.' 
              : 'zum normalen Benutzer machen? Admin-Rechte werden entzogen.'
          }`}
          confirmText="Rolle ändern"
          onConfirm={confirmRoleChange}
          onClose={() => setRoleChangeModal(null)}
        />
      )}

      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}

